import { Search as SearchIcon, SwapHoriz } from '@mui/icons-material';
import { Box, Button, InputAdornment, Menu, MenuItem, TextField, Typography } from '@mui/material';
import ResponsiveTooltip from '../../ResponsiveTooltip';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Transaction,
    TransactionType,
    TransactionTypeGenericType,
    TransactionTypeValue,
} from '../../../services/transaction.types';

interface ChangeTypeActionProps {
    types: TransactionType[];
    onTypeChange: (typeId: number) => void;
    selectedTransactions: Transaction[];
    selectedTransactionGenericTypes: TransactionTypeGenericType;
}

const ChangeTypeAction: React.FC<ChangeTypeActionProps> = ({
    types,
    onTypeChange,
    selectedTransactions,
    selectedTransactionGenericTypes,
}) => {
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [searchQuery, setSearchQuery] = useState('');

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setSearchQuery('');
    };

    const handleTypeSelect = (typeId: number) => {
        onTypeChange(typeId);
        handleClose();
    };

    const filteredTypes = useMemo(() => {
        let filtered = types;
        if (selectedTransactionGenericTypes === TransactionTypeGenericType.In) {
            filtered = filtered.filter((type) => type.in && !type.out);
        } else if (selectedTransactionGenericTypes === TransactionTypeGenericType.Out) {
            filtered = filtered.filter((type) => type.out && !type.in);
        }
        return filtered.filter((type) => type.name.toLowerCase().includes(searchQuery.toLowerCase()));
    }, [types, searchQuery, selectedTransactionGenericTypes]);

    const { isEnabled, disabledReason } = useMemo(() => {
        if (selectedTransactions.length === 0) {
            return { isEnabled: false, disabledReason: t('transactions.changeType.errors.noTransactions') };
        }

        // Get the first transaction's type
        const firstType = selectedTransactions[0].type;

        // Check if all selected transactions are of the same direction (in/out)
        const isFirstIn = firstType.in;
        const isFirstOut = firstType.out;

        const allSameDirection = selectedTransactions.every((transaction) => {
            const currentType = transaction.type;
            return (isFirstIn && currentType.in) || (isFirstOut && currentType.out);
        });

        // Check if any transaction is a trade
        const hasTrade = selectedTransactions.some(
            (transaction) =>
                transaction.typeId === TransactionTypeValue.Trade ||
                transaction.typeId === TransactionTypeValue.SwapWrapNonTaxable
        );

        if (hasTrade) {
            return {
                isEnabled: false,
                disabledReason: t('transactions.changeType.errors.tradeTransactions'),
            };
        }

        if (!allSameDirection) {
            return {
                isEnabled: false,
                disabledReason: t('transactions.changeType.errors.mixedDirections'),
            };
        }

        return { isEnabled: true, disabledReason: '' };
    }, [selectedTransactions]);

    const open = Boolean(anchorEl);

    // Filter available types based on the selected transactions' direction (in/out)
    const availableTypes = useMemo(() => {
        if (selectedTransactions.length === 0) return types;

        const firstType = selectedTransactions[0].type;
        return types.filter(
            (type) => (firstType.in && type.in && !type.out) || (firstType.out && type.out && !type.in)
        );
    }, [types, selectedTransactions]);

    return (
        <>
            <ResponsiveTooltip title={!isEnabled ? disabledReason : ''}>
                <span>
                    <Button
                        startIcon={<SwapHoriz />}
                        onClick={handleClick}
                        variant="text"
                        size="small"
                        disabled={!isEnabled}
                        sx={{ width: { xs: '100%', sm: '100%', md: 'auto' } }}
                    >
                        {t('transactions.changeType.buttonLabel')}
                    </Button>
                </span>
            </ResponsiveTooltip>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                PaperProps={{
                    sx: {
                        width: 300,
                        maxHeight: 400,
                        overflow: 'hidden',
                        mt: 1,
                        bgcolor: (theme) => theme.palette.background.paper,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: (theme) => theme.palette.divider,
                        boxShadow: (theme) => `0px 5px 15px ${theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
                    },
                }}
            >
                <Box sx={{
                    p: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                }}>
                    <TextField
                        size="small"
                        fullWidth
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={t('transactions.changeType.searchPlaceholder')}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" sx={{
                                        color: 'text.secondary',
                                        fontSize: '1.25rem'
                                    }} />
                                </InputAdornment>
                            ),
                            sx: {
                                borderRadius: 1,
                                fontSize: '0.875rem'
                            },
                        }}
                    />
                </Box>
                <Box sx={{
                    maxHeight: 320,
                    overflow: 'auto'
                }}>
                    {filteredTypes
                        .filter((type) => availableTypes.some((t) => t.value === type.value))
                        .map((type) => (
                            <MenuItem
                                key={type.value}
                                onClick={() => handleTypeSelect(type.value)}
                                sx={{
                                    py: 1,
                                    px: 2
                                }}
                            >
                                <Typography variant="body2">{type.name}</Typography>
                            </MenuItem>
                        ))}
                </Box>
            </Menu>
        </>
    );
};

export default ChangeTypeAction;
